import baustein from "baustein";
import { base64toJson } from "web/script/modules/jinja-filters";
import rothko from "web/script/modules/rothko";
import logging from "web/script/utils/logging";
import analytics from "web/script/analytics/analytics";
import doubleclickEvents from "web/script/analytics/doubleclick-events";
import facebookEvents from "web/script/analytics/facebook-events";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import browser from "web/script/utils/browser";
import history from "web/script/utils/history";
import overlayMixin from "../mixins/overlay-mixin";

export default baustein.register("product-overlay", {
    mixins: [overlayMixin],

    defaultOptions: {
        // overlay-mixin options
        overlaySize: "large",
        noContentPadding: true,

        // product-overlay options
        productSlug: null,
        productUid: null,
        linkId: null,
    },

    setupEvents(add) {
        add("openOverlay", "product-card", this._onOpenProductOverlay);
        add("selected", "product-card", this._onProductCardSelect);
        add("openOverlay", "product-color", this._onOpenProductOverlay);
        add("selected", "product-color", this._onProductCardSelect);
        add("addedToBag", this._onAddedToBag);
    },

    onOptionsChange() {
        if (!this.el) {
            return;
        }

        this.el.scrollTop = 0;

        this.getOverlayContent()
            .then(this.setContent.bind(this))
            .then(this.trackPageView.bind(this));
    },

    onOverlayOpen() {
        this.trackPageView();
        // Fix page on mobile to avoid background scrolling on ios
        if (browser.getViewport() === browser.VIEWPORTS.MOBILE) {
            this.emit("fixPageContent");
        }
    },

    onOverlayCloseIntent() {
        history.back();
        if (browser.getViewport() === browser.VIEWPORTS.MOBILE) {
            this.emit("unFixPageContent");
        }
    },

    trackPageView() {
        const container = this.el.querySelector(".product-overlay");
        if (!container) {
            console.warn("Cannot track page view for product overlay!");
            return;
        }

        const analyticsProductData = base64toJson(container.getAttribute("data-analytics-product"));

        // Save the seen product in the
        // user localstorage profile
        userProfiler.saveSeenProduct(analyticsProductData);

        // update environment variables
        environment.set({
            gaVariables: base64toJson(container.getAttribute("data-ga-dimensions")),
            analyticsProduct: analyticsProductData,
            pageType: "product_overlay",
            pageSubType: analyticsProductData.soldout ? "out_of_stock" : "in_stock",
            pageViewAnalytics: base64toJson(container.getAttribute("data-page-view")),
        });

        // we add adroll via tag manager so don't know if it's there yet and we only want to use
        // them temporary so we are doing this the ugly way. will be better with doubleclick (haha, lol)
        try {
            window.__adroll.record_user({
                product_id: analyticsProductData.product_slug,
            });
        } catch (err) {
            // do nothing
        }

        analytics.tagEvent(analytics.TAG_NETWORKS.FACEBOOK, facebookEvents.VIEW_CONTENT, {
            content_ids: [analyticsProductData.product_id],
            content_type: "product",
        });
        analytics.tagEvent(analytics.TAG_NETWORKS.DOUBLECLICK, doubleclickEvents.PRODUCT, {
            id: analyticsProductData.product_id,
            path: analyticsProductData.product_canonical_link,
        });
        analytics.pageView();
    },

    getOverlayContent() {
        const slug = this.options.productSlug;
        const uid = this.options.productUid;
        const linkId = this.options.linkId;

        return Promise.all([
            import(
                /* webpackChunkName: "template-product-overlay" */
                "templates/modules/product/product_overlay"
            ),
            rothko.fetch("modules/product/product_overlay", { slug, uid, link_id: linkId }),
        ])
            .then(([{ default: template }, productOverlayContext]) => {
                this.overlayContext = productOverlayContext;
                return template({
                    product_overlay: this.overlayContext,
                    csrf_token: rothko.getCSRF(),
                });
            })
            .catch((err) => {
                logging.error(err);
            });
    },

    getOverlayContext() {
        return this.overlayContext;
    },

    _onOpenProductOverlay(event) {
        history.replaceState(event.url, event.queryParams);
    },

    /**
     * Handles the 'selected' event from product-card components. Stops propagation so the event
     * does not bubble up any further.
     * @private
     */
    _onProductCardSelect(event) {
        event.stopPropagation();
    },

    _onAddedToBag() {
        history.back();
    },
});
